import React from 'react';
import { 
    Modal, ModalHeader, ModalBody, Row, Col, 
    Table, Spinner, UncontrolledCarousel 
} from 'reactstrap';
import moment from 'moment';
import styles from './UserDetailsModal.module.css';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';

// Helper component for consistent info items
const InfoItem = ({ label, value, highlight }) => (
    <div className={styles.infoItem}>
        <div className={styles.label}>{label}</div>
        <div className={`${styles.value} ${highlight ? 'info-value-highlight' : ''}`}>
            {value}
        </div>
    </div>
);

const UserDetailsModal = ({ 
    isOpen, 
    toggle, 
    user, 
    loading,
    getImageUrl = (url) => {
        if (!url) return '/images/default-avatar.png';
        if (url.startsWith('http')) return url;
        const baseUrl = process.env.REACT_APP_MEDIA_URL.replace(/\/$/, '');
        const imagePath = url.replace(/^\//, '');
        return `${baseUrl}/${imagePath}`;
    }
}) => {
    // Update the styles object
    const styles = {
        mainContainer: {
            backgroundColor: '#ffffff',
            borderRadius: '15px',
            padding: '20px',
            marginBottom: '24px'
        },
        carouselContainer: {
            width: '100%',
            maxWidth: '500px',
            margin: '0 auto',
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: '#f8f9fa',
            height: '405px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        carouselImage: {
            width: '100%',
            height: '500px',
            objectFit: 'contain',
            backgroundColor: '#f8f9fa',
            padding: '10px'
        },
        infoSection: {
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
            border: '1px solid #e9ebec'
        },
        sectionTitle: {
            color: '#405189',
            fontSize: '15px',
            fontWeight: '600',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            paddingBottom: '10px',
            borderBottom: '1px dashed #e9ebec'
        },
        infoGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px'
        },
        badge: {
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
        },
        chip: {
            display: 'inline-flex',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            backgroundColor: '#405189',
            color: '#fff',
            margin: '2px',
            alignItems: 'center',
            gap: '4px'
        },
        modalBody: {
            maxHeight: '80vh',
            overflowY: 'auto'
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" className="user-details-modal">
            <ModalHeader toggle={false} className="d-flex align-items-center border-bottom">
                <div className="d-flex align-items-center">
                    <i className="ri-user-line me-2"></i>
                    <span>User Profile</span>
                </div>
            </ModalHeader>
            <button 
                type="button" 
                onClick={toggle}
                className="btn-close btn-close-dark"
                style={{ 
                    position: 'absolute',
                    right: '30px',
                    top: '20px',
                    zIndex: 1
                }}
                aria-label="Close"
            ></button>
            <ModalBody>
                <SimpleBar style={{ maxHeight: '600px' }}>
                    {loading ? (
                        <div className="text-center p-4">
                            <Spinner color="primary"> Loading... </Spinner>
                        </div>
                    ) : user && (
                        <>
                            <Row className="g-4" style={{ width: '98%' }}>
                                <Col lg={6}>
                                    {/* Carousel Column */}
                                    {user.profilePic && user.profilePic.length > 0 && (
                                        <div style={styles.carouselContainer}>
                                            <UncontrolledCarousel
                                                interval={4000}
                                                items={user.profilePic
                                                    .filter(pic => pic.type === 'PHOTO')
                                                    .map((pic, index) => ({
                                                        altText: `${user.firstName}'s photo ${index + 1}`,
                                                        caption: '',
                                                        key: index,
                                                        src: getImageUrl(pic.url)
                                                    }))}
                                                className="user-carousel"
                                            />
                                        </div>
                                    )}
                                    
                                    {/* Additional Info Section moved here */}
                                    <div style={styles.infoSection} className="info-section-wrapper mt-4">
                                        <div style={styles.sectionTitle}>
                                            <i className="ri-information-line section-icon"></i>
                                            Additional Information
                                        </div>
                                        <div style={{...styles.infoGrid, gridTemplateColumns: 'repeat(2, 1fr)'}}>
                                            <InfoItem label="User Type" value={user.userTypeText} />
                                            <InfoItem label="Social Media" value={user.socialMediaLink !== 'N/A' ? user.socialMediaLink : 'Not provided'} />
                                            <InfoItem label="Website" value={user.website !== 'N/A' ? user.website : 'Not provided'} />
                                            <InfoItem label="Address" value={user.address !== 'N/A' ? user.address : 'Not provided'} />
                                            <InfoItem label="Shoutout Price" value={user.shoutoutPrice !== 'N/A' ? user.shoutoutPrice : 'Not set'} />
                                            <InfoItem 
                                                label="Search Preference" 
                                                value={
                                                    <div>
                                                        {user.searchPreferenceGender?.map((pref, index) => (
                                                            <span key={index} style={styles.chip}>
                                                                <i className="ri-user-heart-line me-1"></i>
                                                                {pref}
                                                            </span>
                                                        ))}
                                                    </div>
                                                } 
                                            />
                                        </div>
                                    </div>
                                </Col>

                                {/* Right Column with Basic Info and Account Details */}
                                <Col lg={6}>
                                    <div style={styles.infoSection} className="info-section-wrapper">
                                        <div style={styles.sectionTitle}>
                                            <i className="ri-user-3-line section-icon"></i>
                                            Basic Information
                                        </div>
                                        <div style={styles.infoGrid}>
                                            <InfoItem label="Full Name" value={`${user.firstName} ${user.lastName !== 'N/A' ? user.lastName : ''}`} />
                                            <InfoItem label="Username" value={`@${user.username}`} />
                                            <InfoItem label="Email" value={user.email} />
                                            <InfoItem label="Phone" value={user.phoneNumber ? `${user.countryCode} ${user.phoneNumber}` : 'Not provided'} />
                                            <InfoItem label="Gender" value={user.gender} />
                                            <InfoItem label="Date of Birth" value={moment(user.dateOfBirth).format('DD MMM YYYY')} />
                                            <InfoItem label="Country" value={user.country || 'Not specified'} />
                                            <InfoItem label="City" value={user.city || 'Not specified'} />
                                        </div>
                                    </div>

                                    <div style={styles.infoSection} className="info-section-wrapper">
                                        <div style={styles.sectionTitle}>
                                            <i className="ri-profile-line section-icon"></i>
                                            Account Details
                                        </div>
                                        <div style={styles.infoGrid}>
                                            <InfoItem 
                                                label="Status" 
                                                value={
                                                    <span style={{
                                                        ...styles.badge,
                                                        backgroundColor: user.statusText === 'ACTIVE' ? '#daf4e6' : '#fde8e8',
                                                        color: user.statusText === 'ACTIVE' ? '#0ab39c' : '#f06548'
                                                    }}>
                                                        <i className={`ri-${user.statusText === 'ACTIVE' ? 'checkbox-circle' : 'close-circle'}-fill`}></i>
                                                        {user.statusText}
                                                    </span>
                                                } 
                                            />
                                            <InfoItem 
                                                label="Profile Verified" 
                                                value={
                                                    <i className={`ri-${user.profileVerified ? 'checkbox-circle-fill text-success' : 'close-circle-fill text-danger'} fs-16`}></i>
                                                } 
                                            />
                                            <InfoItem 
                                                label="Token Balance" 
                                                value={
                                                    <span className="d-flex align-items-center">
                                                        <span className="fw-semibold">{user.tokenBalance}</span>
                                                        <i className="ri-coin-line text-warning ms-1"></i>
                                                    </span>
                                                }
                                                highlight
                                            />
                                            <InfoItem label="Referral Code" value={user.referralCode} />
                                            <InfoItem label="Referral Count" value={user.referralCount} />
                                            <InfoItem label="Likes Count" value={user.likesCount} />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </SimpleBar>
            </ModalBody>
        </Modal>
    );
};

export default UserDetailsModal;

<style>
    {`
    .user-details-modal .modal-dialog {
        max-width: 90%;
        margin: 1.75rem auto;
    }
    .user-details-modal .modal-content {
        min-height: 80vh;
        max-height: 90vh;
        width: 100%;
        overflow-x: hidden;
    }
    .user-details-modal .modal-body {
        padding: 1.5rem;
        overflow-x: hidden;
    }
    .user-details-modal .simplebar-content {
        padding-right: 0 !important;
        padding-bottom: 0 !important;
    }
    .user-details-modal .simplebar-track.simplebar-horizontal {
        display: none !important;
    }
    .user-details-modal .simplebar-content-wrapper {
        overflow-x: hidden !important;
    }
    .user-details-modal .info-section-wrapper {
        width: 100%;
        overflow-x: hidden;
    }
    .user-details-modal .row {
        margin-right: 0;
        margin-left: 0;
        width: 100%;
    }
    .user-details-modal .col-lg-6 {
        padding-right: 12px;
        padding-left: 12px;
    }
    `}
</style> 